'use client'
import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MultiCommuneSelect } from '@/components/ui/multi-commune-select'
import { MultiWillayaSelect } from '@/components/ui/multi-willaya-select'
import { Search, X } from 'lucide-react'
import AlgerianWilaya from '@/utilities/data/Algeria_Willaya.json'
import AlgeriaCities from '@/utilities/data/Algeria_Cities.json'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/utilities/ui'
import { useTheme } from '@/providers/Theme'
import { useRouter, usePathname } from 'next/navigation'
import router from 'next/router'

const AdvancedSearchModal = ({
  isOpen,
  onClose,
  title,
  searchText = '',
  listedIn = 'for_sale',
  dictionary,
  lang = 'en',
}: {
  isOpen: boolean
  onClose: () => void
  title?: string
  searchText?: string
  listedIn?: string
  dictionary?: any
  lang: string
}) => {
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [minSurface, setMinSurface] = useState('')
  const [maxSurface, setMaxSurface] = useState('')
  const [rooms, setRooms] = useState<string[]>([])
  const [amenities, setAmenities] = useState<string[]>([])
  const [selectedWillayas, setSelectedWillayas] = useState<string[]>([])
  const [selectedCities, setSelectedCities] = useState<string[]>([])
  const [isWillayaPopoverOpen, setIsWillayaPopoverOpen] = useState(false)
  const [isCityPopoverOpen, setIsCityPopoverOpen] = useState(false)
  const router = useRouter()

  // Check if RTL
  const isRTL = lang === 'ar'

  // Get room options with translations
  const roomOptions = [
    { value: 'any', label: dictionary?.propertyListing?.rooms?.any || 'Any' },
    { value: 1, label: dictionary?.propertyListing?.rooms?.[1] || '1' },
    { value: 2, label: dictionary?.propertyListing?.rooms?.[2] || '2' },
    { value: 3, label: dictionary?.propertyListing?.rooms?.[3] || '3' },
    { value: 4, label: dictionary?.propertyListing?.rooms?.[4] || '4' },
    { value: 5, label: dictionary?.propertyListing?.rooms?.[5] || '5' },
    { value: 6, label: dictionary?.propertyListing?.rooms?.[6] || '5+' },
  ]

  // Feature list with translations
  const FeatureSearchList = [
    {
      feature: dictionary?.propertyListing?.features?.generator || 'Generator',
      value: 'generator',
    },
    {
      feature: dictionary?.propertyListing?.features?.waterTank || 'Water Tank',
      value: 'waterTank',
    },
    {
      feature: dictionary?.propertyListing?.features?.gasConnection || 'Gas Connection',
      value: 'gasConnection',
    },
    {
      feature: dictionary?.propertyListing?.features?.garage || 'Garage',
      value: 'garage',
    },
    {
      feature: dictionary?.propertyListing?.features?.balcony || 'Balcony',
      value: 'balcony',
    },
    {
      feature: dictionary?.propertyListing?.features?.elevator || 'Elevator',
      value: 'elevator',
    },
    {
      feature: dictionary?.propertyListing?.features?.garden || 'Garden',
      value: 'garden',
    },
    {
      feature: dictionary?.propertyListing?.features?.terrace || 'Terrace',
      value: 'terrace',
    },
    {
      feature: dictionary?.propertyListing?.features?.nearbyMosque || 'Nearby Mosque',
      value: 'nearbyMosque',
    },
    {
      feature: dictionary?.propertyListing?.features?.sharedParking || 'Shared Parking',
      value: 'sharedParking',
    },
    {
      feature: dictionary?.propertyListing?.features?.airConditioning || 'Air Conditioning',
      value: 'airConditioning',
    },
    {
      feature: dictionary?.propertyListing?.features?.heater || 'Heater',
      value: 'heater',
    },
    {
      feature: dictionary?.propertyListing?.features?.frontYard || 'Front Yard',
      value: 'frontYard',
    },
    {
      feature: dictionary?.propertyListing?.features?.gym || 'Gym',
      value: 'gym',
    },
    {
      feature: dictionary?.propertyListing?.features?.laundry || 'Laundry',
      value: 'laundry',
    },
    {
      feature: dictionary?.propertyListing?.features?.privateSpace || 'Private Space',
      value: 'privateSpace',
    },
    {
      feature: dictionary?.propertyListing?.features?.refrigerator || 'Refrigerator',
      value: 'refrigerator',
    },
    {
      feature: dictionary?.propertyListing?.features?.sauna || 'Sauna',
      value: 'sauna',
    },
    {
      feature: dictionary?.propertyListing?.features?.seaView || 'Sea View',
      value: 'seaView',
    },
    {
      feature: dictionary?.propertyListing?.features?.wifi || 'Wifi',
      value: 'wifi',
    },
  ]

  // Get cities for selected willayas
  const availableCities = useMemo(() => {
    return AlgeriaCities.filter((city) => selectedWillayas.includes(city.wilaya_id))
  }, [selectedWillayas])

  // Remove selected cities that are not in the available cities anymore
  useEffect(() => {
    const validCityIds = availableCities.map((city) => city.id)
    setSelectedCities((prev) => prev.filter((cityId) => validCityIds.includes(cityId)))
  }, [availableCities])

  // Handle form submission - construct URL with all parameters
  const updateSearchParams = () => {
    const params = new URLSearchParams()

    if (selectedWillayas.length > 0) params.set('wilaya', selectedWillayas.join(','))
    if (selectedCities.length > 0) params.set('cities', selectedCities.join(','))
    if (minPrice) params.set('minPrice', minPrice.toString())
    if (maxPrice) params.set('maxPrice', maxPrice.toString())
    if (minSurface) params.set('minSurface', minSurface.toString())
    if (maxSurface) params.set('maxSurface', maxSurface.toString())
    if (rooms.length > 0) params.set('rooms', rooms.join(','))
    if (amenities.length > 0) params.set('amenities', amenities.join(','))
    params.set('search', searchText)
    params.set('listedIn', listedIn)

    // Navigate with search params including language
    router.push(`/${lang}/properties?${params.toString()}`)
  }

  // Filter handlers
  const handleMinPriceChange = (value) => {
    setMinPrice(value)
  }

  const handleMaxPriceChange = (value) => {
    setMaxPrice(value)
  }

  const handleMinSurfaceChange = (value) => {
    setMinSurface(value)
  }

  const handleMaxSurfaceChange = (value) => {
    setMaxSurface(value)
  }

  const handleRoomsChange = (value: string) => {
    // Clicking "Any" clears all selections
    if (value === 'any') {
      setRooms([])
      return
    }

    const v = String(value)

    const nextRooms = rooms.includes(v)
      ? rooms.filter((x) => x !== v) // remove
      : [...rooms, v] // add

    // Keep numeric order for stable URLs / state
    nextRooms.sort((a, b) => Number(a) - Number(b))

    setRooms(nextRooms)
  }

  const toggleAmenity = (amenity: string) => {
    setAmenities((prev) => {
      if (prev.includes(amenity)) {
        return prev.filter((item) => item !== amenity)
      } else {
        return [...prev, amenity]
      }
    })
  }

  // Reset all filters
  const handleReset = () => {
    setSelectedWillayas([])
    setSelectedCities([])
    setMinPrice('')
    setMaxPrice('')
    setMinSurface('')
    setMaxSurface('')
    setRooms([])
    setAmenities([])
  }

  const { theme } = useTheme()

  // Close when pressing Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-card rounded-md shadow-lg md:max-w-[690px] lg:min-w-[690px] max-w-[90%] max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className={cn(
            'absolute top-4 p-2 rounded-full bg-gray-200 hover:bg-gray-300',
            isRTL ? 'left-6' : 'right-6',
          )}
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
        <div className={cn('h-20 flex items-center px-[30px] border-b', isRTL && 'text-right')}>
          {title && <h2 className="text-xl font-bold text-typography">{title}</h2>}
        </div>
        <div className="">
          <div className="flex flex-col gap-6 py-4 px-[30px]">
            {/* Price Range */}
            <div className="space-y-2">
              <Label className={cn('text-typography font-medium block', isRTL && 'text-right')}>
                {dictionary?.propertyListing?.asideFilter?.priceRange || 'Price Range (DZD)'}
              </Label>
              <div className={cn('flex items-center gap-2', isRTL && 'flex-row-reverse')}>
                <Input
                  type="text"
                  placeholder={dictionary?.propertyListing?.asideFilter?.minPrice || '$ min.'}
                  value={minPrice}
                  onChange={(e) => handleMinPriceChange(e.target.value)}
                  className={cn('rounded-md md:h-[50px]', isRTL && 'text-right')}
                />
                <span className="text-typography">—</span>
                <Input
                  type="text"
                  placeholder={dictionary?.propertyListing?.asideFilter?.maxPrice || '$ max.'}
                  value={maxPrice}
                  onChange={(e) => handleMaxPriceChange(e.target.value)}
                  className={cn('rounded-md md:h-[50px]', isRTL && 'text-right')}
                />
              </div>
            </div>

            {/* Surface */}
            <div className="space-y-2">
              <Label className={cn('text-typography font-medium block', isRTL && 'text-right')}>
                {dictionary?.propertyListing?.asideFilter?.surface || 'Surface (m²)'}
              </Label>
              <div className={cn('flex items-center gap-2', isRTL && 'flex-row-reverse')}>
                <Input
                  type="text"
                  placeholder={dictionary?.propertyListing?.asideFilter?.min || 'Min.'}
                  value={minSurface}
                  onChange={(e) => handleMinSurfaceChange(e.target.value)}
                  className={cn('rounded-md md:h-[50px]', isRTL && 'text-right')}
                />
                <span className="text-typography">—</span>
                <Input
                  type="text"
                  placeholder={dictionary?.propertyListing?.asideFilter?.max || 'Max.'}
                  value={maxSurface}
                  onChange={(e) => handleMaxSurfaceChange(e.target.value)}
                  className={cn('rounded-md md:h-[50px]', isRTL && 'text-right')}
                />
              </div>
            </div>

            {/* Location */}
            <div className="advenceSearchModal grid md:grid-cols-2 gap-4">
              <div className="text-typography relative space-y-2">
                <Label className={cn('font-medium block', isRTL && 'text-right')}>
                  {dictionary?.propertyListing?.asideFilter?.willaya || 'Wilaya'}
                </Label>
                <MultiCommuneSelect
                  options={AlgerianWilaya}
                  onValueChange={setSelectedWillayas}
                  value={selectedWillayas}
                  initialSelectedValues={selectedWillayas}
                  placeholder={
                    dictionary?.propertyListing?.asideFilter?.selectWillaya ||
                    'Select willaya options'
                  }
                  variant="default"
                  animation={2}
                  maxCount={3}
                  isPopoverOpen={isWillayaPopoverOpen}
                  setIsPopoverOpen={setIsWillayaPopoverOpen}
                />
              </div>
              <div className="relative space-y-2">
                <Label className={cn('text-typography font-medium block', isRTL && 'text-right')}>
                  {dictionary?.propertyListing?.asideFilter?.city || 'City'}
                </Label>
                <MultiCommuneSelect
                  options={availableCities}
                  onValueChange={setSelectedCities}
                  value={selectedCities}
                  initialSelectedValues={selectedCities}
                  placeholder={
                    dictionary?.propertyListing?.asideFilter?.selectCity || 'Select city options'
                  }
                  variant="default"
                  animation={2}
                  maxCount={3}
                  isPopoverOpen={isCityPopoverOpen}
                  setIsPopoverOpen={setIsCityPopoverOpen}
                  className="!bg-transparent"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-1 gap-4">
              {/* Units */}
              <div className={cn('space-y-2', isRTL ? 'md:col-span-2' : '')}>
                <Label className={cn('text-typography font-medium block', isRTL && 'text-right')}>
                  {dictionary?.propertyListing?.asideFilter?.rooms || 'Units'}
                </Label>
                <div
                  className={cn(
                    'flex overflow-hidden rounded-xl h-auto',
                    isRTL && 'flex-wrap gap-2 h-auto',
                  )}
                >
                  {isRTL
                    ? // RTL: Individual buttons with proper spacing
                      roomOptions.map((option) => {
                        const isAnySelected = rooms.length === 0

                        const isSelected =
                          option.value === 'any'
                            ? isAnySelected
                            : rooms.includes(String(option.value))
                        return (
                          <button
                            key={option.value}
                            className={cn(
                              'text-typography py-2 px-4 text-center transition-colors rounded-xl border-2 flex-1 min-w-0 text-sm',
                              isSelected
                                ? 'border-typography z-20 bg-primary/10'
                                : 'bg-inputBackground hover:bg-light border-slate-200/50 z-10',
                            )}
                            onClick={() => handleRoomsChange(String(option.value))}
                          >
                            {option.label}
                          </button>
                        )
                      })
                    : // LTR: Connected buttons
                      roomOptions.map((option) => {
                        const isAnySelected = rooms.length === 0

                        const isSelected =
                          option.value === 'any'
                            ? isAnySelected
                            : rooms.includes(String(option.value))
                        return (
                          <button
                            key={option.value}
                            className={cn(
                              'text-typography first-of-type:mx-0 last-of-type:mx-0 mx-[-1px] flex-1 py-2 px-3 text-center transition-colors first-of-type:rounded-l-xl last-of-type:rounded-r-xl',
                              isSelected
                                ? 'border-2 border-typography z-20'
                                : 'bg-inputBackground hover:bg-light border-2 border-slate-200/50 z-10',
                            )}
                            onClick={() => handleRoomsChange(String(option.value))}
                          >
                            {option.label === dictionary?.propertyListing?.rooms?.any
                              ? option.label
                              : 'F' + option.label}
                          </button>
                        )
                      })}
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="space-y-3">
              <Label className={cn('text-typography font-medium block', isRTL && 'text-right')}>
                {dictionary?.propertyListing?.asideFilter?.amenities || 'Amenities'}
              </Label>
              <div className="grid lg:grid-cols-3 grid-cols-2 lg:gap-y-6 gap-y-4">
                {FeatureSearchList.map((feature) => (
                  <div key={feature.value} className={cn('flex items-center')}>
                    <Checkbox
                      checked={amenities.includes(feature.value)}
                      onCheckedChange={() => toggleAmenity(feature.value)}
                      id={feature.value}
                      className={cn(isRTL ? 'ml-2' : 'mr-2')}
                    />
                    <Label className="text-typography" htmlFor={feature.value}>
                      {feature.feature}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className={cn(
              'flex items-center justify-between border-t p-4',
              isRTL && 'flex-row-reverse',
            )}
          >
            <Button
              variant="link"
              onClick={handleReset}
              className="flex items-center text-typography hover:text-primary transition-colors duration-200"
            >
              {theme === 'dark' ? (
                <img
                  src="/icons/back-w.svg"
                  alt="back svg"
                  className={cn('fill-white stroke-white', isRTL ? 'ml-2 scale-x-[-1]' : 'mr-2')}
                />
              ) : (
                <img
                  src="/icons/back.svg"
                  alt="back svg"
                  className={cn('fill-white stroke-white', isRTL ? 'ml-2 scale-x-[-1]' : 'mr-2')}
                />
              )}
              {dictionary?.propertyListing?.asideFilter?.resetAllFilters || 'Reset all filters'}
            </Button>
            <Button
              onClick={updateSearchParams}
              className={cn(
                'md:w-[165px] w-[120px] md:h-[55px] h-[45px] rounded-xl bg-primary hover:bg-primary-200 transition-colors duration-200 text-white text-sm font-semibold flex items-center',
                isRTL && 'flex-row-reverse',
              )}
            >
              <Search className={cn(isRTL ? 'ml-2' : 'mr-2')} />
              {dictionary?.propertyListing?.asideFilter?.search || 'Search'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdvancedSearchModal
